import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Mic,
  Plug,
  BarChart3,
  CheckCircle2,
  Loader2,
  AlertCircle,
  SkipForward,
  Rocket,
  Volume2,
} from 'lucide-react'
import { JarvisCard } from '@/components/JarvisCard'
import { useToast } from '@/components/Toast'
import { JarvisOrb } from '@/components/JarvisOrb'
import { apiClient, unwrap } from '@/lib/apiClient'
import { useConnections, useCurrentUser } from '@/lib/hooks'
import { HAS_AUTH } from '@/lib/auth'
import { usePersona } from '@/lib/personas'
import type { ConnectionStatus, User } from '@/types'
import { cn } from '@/lib/cn'

type StepId = 'company' | 'voice' | 'ads' | 'metrics'

interface StepDef {
  id: StepId
  title: string
  subtitle: string
  Icon: typeof Building2
  required: boolean
}

const STEPS: StepDef[] = [
  {
    id: 'company',
    title: 'Tell us about your business',
    subtitle: 'We use this to ground every conversation in your context.',
    Icon: Building2,
    required: true,
  },
  {
    id: 'voice',
    title: 'Choose your AI voice',
    subtitle: 'How your AI will sound on the phone. You can change this any time.',
    Icon: Mic,
    required: true,
  },
  {
    id: 'ads',
    title: 'Connect Facebook Ads',
    subtitle: 'Optional. Connect now to unlock daily ad intelligence in your briefings.',
    Icon: BarChart3,
    required: false,
  },
  {
    id: 'metrics',
    title: 'Connect Stripe metrics',
    subtitle: 'Optional. Connect now to see MRR, churn, and trial status at a glance.',
    Icon: Plug,
    required: false,
  },
]

interface PlatformVoice {
  voiceId: string
  name: string
  persona: 'jarvis' | 'ultron' | 'ares'
  description: string
  blurb: string
}

interface VoiceSettings {
  voices: PlatformVoice[]
  currentVoiceId: string | null
  effectiveVoiceId: string
  agentId: string | null
  voiceEnabled: boolean
}

interface SettingsResponse {
  settings: User['settings']
  systemPrompt: string
}

export function SetupWizard() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const { persona } = usePersona()

  const [stepIdx, setStepIdx] = useState(0)
  const step = STEPS[stepIdx]
  const isLast = stepIdx === STEPS.length - 1

  // Load existing data so the wizard preserves work in progress.
  const { data: settingsData } = useQuery<SettingsResponse>({
    queryKey: ['settings'],
    queryFn: () => unwrap<SettingsResponse>(apiClient.get('/settings')),
    enabled: HAS_AUTH,
  })
  const { data: voiceData } = useQuery<VoiceSettings>({
    queryKey: ['voice-settings'],
    queryFn: () => unwrap<VoiceSettings>(apiClient.get('/setup/voice')),
    enabled: HAS_AUTH,
  })
  const { data: connectionsData } = useConnections()
  const connections = connectionsData?.items ?? []

  // Step 1 — company info
  const [companyName, setCompanyName] = useState('')
  const [companyDescription, setCompanyDescription] = useState('')
  const [preferredName, setPreferredName] = useState('')

  // Enrichment-driven pre-fill — if Apollo/scraper found these values during
  // first-login enrichment, they show up here so the user just confirms.
  const { data: me } = useCurrentUser()
  const enrichment = me?.enrichment ?? null
  const enrichedDefaults = {
    companyName: enrichment?.company?.name ?? '',
    companyDescription:
      enrichment?.website?.businessSummary ?? enrichment?.company?.description ?? '',
    preferredName: enrichment?.person?.firstName ?? '',
  }

  useEffect(() => {
    if (!settingsData) return
    setCompanyName(settingsData.settings.companyName || enrichedDefaults.companyName || '')
    setCompanyDescription(
      settingsData.settings.companyDescription || enrichedDefaults.companyDescription || '',
    )
    setPreferredName(settingsData.settings.preferredName || enrichedDefaults.preferredName || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsData, enrichment])

  // Step 2 — voice
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState<string | null>(null)

  useEffect(() => {
    if (!voiceData) return
    setSelectedVoiceId(voiceData.effectiveVoiceId ?? null)
  }, [voiceData])

  // Step 3 — Facebook
  const [fbToken, setFbToken] = useState('')
  const [fbAdAccount, setFbAdAccount] = useState('')

  // Step 4 — Stripe
  const [stripeSecret, setStripeSecret] = useState('')

  // Mutations
  const saveCompany = useMutation({
    mutationFn: () =>
      unwrap(
        apiClient.patch('/settings', {
          companyName,
          companyDescription,
          ...(preferredName ? { preferredName } : {}),
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
  })

  const saveVoice = useMutation({
    mutationFn: (voiceId: string) =>
      unwrap(apiClient.post('/setup/voice', { voiceId })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voice-settings'] })
    },
  })

  const previewVoice = useMutation({
    mutationFn: (voiceId: string) =>
      unwrap<{ audioUrl: string }>(
        apiClient.post('/setup/voices/test', { voiceId }),
      ),
    onSuccess: (data, voiceId) => {
      const audio = new Audio(data.audioUrl)
      audio.onended = () => setPreviewing((p) => (p === voiceId ? null : p))
      audio.onerror = () => setPreviewing(null)
      void audio.play().catch(() => setPreviewing(null))
    },
    onError: (e) => {
      setPreviewing(null)
      toast.error('Preview failed', (e as Error).message)
    },
  })

  const saveFacebook = useMutation({
    mutationFn: () =>
      unwrap<ConnectionStatus>(
        apiClient.post('/setup/connections', {
          provider: 'facebook',
          facebook: { systemUserToken: fbToken, adAccountId: fbAdAccount },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['connections'] }),
  })

  const saveStripe = useMutation({
    mutationFn: () =>
      unwrap<ConnectionStatus>(
        apiClient.post('/setup/connections', {
          provider: 'stripe',
          stripe: { secretKey: stripeSecret },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['connections'] }),
  })

  const finish = useMutation({
    mutationFn: () => unwrap(apiClient.post('/users/complete-onboarding')),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
      toast.success(`${persona.wordmark} is live`, 'Welcome to your command center.')
      navigate('/dashboard', { replace: true })
    },
    onError: (e) => toast.error("Couldn't activate", (e as Error).message),
  })

  // Step validation
  const canProceed = (() => {
    if (step.id === 'company') return companyName.trim().length > 0
    if (step.id === 'voice') return Boolean(selectedVoiceId)
    return true // optional steps
  })()

  async function next() {
    try {
      if (step.id === 'company') {
        await saveCompany.mutateAsync()
      } else if (step.id === 'voice' && selectedVoiceId && selectedVoiceId !== voiceData?.currentVoiceId) {
        await saveVoice.mutateAsync(selectedVoiceId)
      } else if (step.id === 'ads' && fbToken.trim() && fbAdAccount.trim()) {
        await saveFacebook.mutateAsync()
      } else if (step.id === 'metrics' && stripeSecret.trim()) {
        await saveStripe.mutateAsync()
      }
    } catch (e) {
      toast.error('Save failed', (e as Error).message)
      return
    }
    if (isLast) {
      await finish.mutateAsync().catch(() => undefined)
    } else {
      setStepIdx((i) => Math.min(i + 1, STEPS.length - 1))
    }
  }

  function skip() {
    if (isLast) {
      void finish.mutateAsync().catch(() => undefined)
    } else {
      setStepIdx((i) => Math.min(i + 1, STEPS.length - 1))
    }
  }

  function back() {
    setStepIdx((i) => Math.max(i - 1, 0))
  }

  const isSaving =
    saveCompany.isPending ||
    saveVoice.isPending ||
    saveFacebook.isPending ||
    saveStripe.isPending ||
    finish.isPending

  const fbConn = connections.find((c) => c.provider === 'facebook')
  const stripeConn = connections.find((c) => c.provider === 'stripe')

  return (
    <div className="min-h-full flex flex-col items-center pt-2 pb-12">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <JarvisOrb state="idle" size="sm" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">
            Let's get {persona.wordmark} online
          </h1>
          <p className="text-sm text-jarvis-muted mt-2">
            Four short steps. Five minutes. Then you're live.
          </p>
        </div>

        {/* Stepper */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((s, i) => {
              const done = i < stepIdx
              const active = i === stepIdx
              return (
                <div
                  key={s.id}
                  className="flex items-center gap-2 flex-1 last:flex-none"
                >
                  <div
                    className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors flex-shrink-0',
                      done && 'bg-jarvis-blue text-black',
                      active && 'bg-jarvis-blue/15 text-jarvis-blue border border-jarvis-blue',
                      !done && !active && 'bg-jarvis-surface-2 text-jarvis-muted border border-jarvis-border',
                    )}
                  >
                    {done ? <CheckCircle2 size={14} /> : i + 1}
                  </div>
                  <div
                    className={cn(
                      'text-[11px] uppercase tracking-widest font-semibold hidden sm:block',
                      active ? 'text-jarvis-text' : 'text-jarvis-muted',
                    )}
                  >
                    {s.id === 'company' && 'Business'}
                    {s.id === 'voice' && 'Voice'}
                    {s.id === 'ads' && 'Ads'}
                    {s.id === 'metrics' && 'Metrics'}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={cn(
                        'flex-1 h-px transition-colors',
                        done ? 'bg-jarvis-blue' : 'bg-jarvis-border',
                      )}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <JarvisCard className="space-y-6">
          {/* Step header */}
          <div className="flex items-start gap-4 pb-4 border-b border-jarvis-border/50">
            <div className="w-12 h-12 rounded-lg bg-jarvis-blue/10 border border-jarvis-blue/30 flex items-center justify-center flex-shrink-0">
              <step.Icon size={20} className="text-jarvis-blue" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest text-jarvis-muted">
                Step {stepIdx + 1} of {STEPS.length}
                {!step.required && (
                  <span className="ml-2 text-jarvis-amber">Optional</span>
                )}
              </div>
              <h2 className="text-xl font-semibold mt-1">{step.title}</h2>
              <p className="text-sm text-jarvis-muted mt-1">{step.subtitle}</p>
            </div>
          </div>

          {/* Step body */}
          {step.id === 'company' && (
            <div className="space-y-5">
              <Field label="Company name" required>
                <input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Operations"
                  className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2.5 text-sm w-full focus:outline-none focus:border-jarvis-blue/60"
                  autoFocus
                />
              </Field>
              <Field
                label="What do you do?"
                hint="One sentence. Your AI uses this to introduce itself and frame every conversation."
              >
                <textarea
                  rows={2}
                  value={companyDescription}
                  onChange={(e) => setCompanyDescription(e.target.value)}
                  placeholder="We sell custom doors and windows to high-end builders in Texas."
                  className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2.5 text-sm w-full focus:outline-none focus:border-jarvis-blue/60 resize-none"
                />
              </Field>
              <Field
                label="What should we call you?"
                hint="Used when your AI greets you on briefings. Defaults to your sign-up name."
              >
                <input
                  value={preferredName}
                  onChange={(e) => setPreferredName(e.target.value)}
                  placeholder="KC"
                  className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2.5 text-sm w-full focus:outline-none focus:border-jarvis-blue/60"
                />
              </Field>
            </div>
          )}

          {step.id === 'voice' && (
            <div className="space-y-3">
              {!voiceData && (
                <div className="text-sm text-jarvis-muted">Loading voices…</div>
              )}
              {voiceData?.voices.map((v) => {
                const isSelected = v.voiceId === selectedVoiceId
                const isPlaying = previewing === v.voiceId
                return (
                  <button
                    key={v.voiceId}
                    type="button"
                    onClick={() => setSelectedVoiceId(v.voiceId)}
                    className={cn(
                      'w-full text-left flex items-start gap-4 p-4 rounded-lg border transition-colors',
                      isSelected
                        ? 'border-jarvis-blue bg-jarvis-blue/10'
                        : 'border-jarvis-border bg-jarvis-surface-2/40 hover:border-jarvis-blue/40',
                    )}
                  >
                    <div
                      className={cn(
                        'w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                        isSelected
                          ? 'bg-jarvis-blue text-black'
                          : 'bg-jarvis-surface border border-jarvis-border text-jarvis-muted',
                      )}
                    >
                      {isSelected ? <CheckCircle2 size={16} /> : <Mic size={16} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{v.name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-jarvis-muted">
                          {v.persona} persona
                        </span>
                      </div>
                      <p className="text-xs text-jarvis-muted mt-1 leading-relaxed">
                        {v.blurb}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPreviewing(v.voiceId)
                        previewVoice.mutate(v.voiceId)
                      }}
                      disabled={previewVoice.isPending}
                      className="flex-shrink-0 text-xs px-3 py-1.5 bg-jarvis-surface border border-jarvis-border rounded-lg hover:bg-jarvis-blue/10 hover:border-jarvis-blue/40 hover:text-jarvis-blue transition-colors flex items-center gap-1.5"
                    >
                      {isPlaying ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Volume2 size={12} />
                      )}
                      Preview
                    </button>
                  </button>
                )
              })}
            </div>
          )}

          {step.id === 'ads' && (
            <div className="space-y-5">
              {fbConn?.status === 'connected' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-jarvis-green/10 border border-jarvis-green/20 rounded-lg text-sm text-jarvis-green">
                  <CheckCircle2 size={14} />
                  Already connected — leave the fields below blank to keep the existing connection.
                </div>
              )}
              <Field
                label="System User token"
                hint="Meta Business Settings → System Users → generate a token with ads_read scope."
              >
                <input
                  type="password"
                  value={fbToken}
                  onChange={(e) => setFbToken(e.target.value)}
                  placeholder="EAAB…"
                  className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2.5 text-sm w-full focus:outline-none focus:border-jarvis-blue/60 font-mono"
                />
              </Field>
              <Field label="Ad account ID" hint="From the top of your Ads Manager URL.">
                <input
                  value={fbAdAccount}
                  onChange={(e) => setFbAdAccount(e.target.value)}
                  placeholder="act_1234567890"
                  className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2.5 text-sm w-full focus:outline-none focus:border-jarvis-blue/60 font-mono"
                />
              </Field>
              {saveFacebook.error && (
                <div className="flex items-start gap-2 text-sm text-jarvis-red">
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{(saveFacebook.error as Error).message}</span>
                </div>
              )}
              <p className="text-xs text-jarvis-muted">
                Skip this if you don't run Facebook Ads. You can connect later from{' '}
                <strong className="text-jarvis-text">Setup</strong> in the sidebar.
              </p>
            </div>
          )}

          {step.id === 'metrics' && (
            <div className="space-y-5">
              {stripeConn?.status === 'connected' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-jarvis-green/10 border border-jarvis-green/20 rounded-lg text-sm text-jarvis-green">
                  <CheckCircle2 size={14} />
                  Already connected — leave the field below blank to keep it.
                </div>
              )}
              <Field
                label="Stripe restricted key"
                hint="Use a restricted key with read-only access to Customers, Subscriptions, and Invoices."
              >
                <input
                  type="password"
                  value={stripeSecret}
                  onChange={(e) => setStripeSecret(e.target.value)}
                  placeholder="rk_live_… or sk_test_…"
                  className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2.5 text-sm w-full focus:outline-none focus:border-jarvis-blue/60 font-mono"
                />
              </Field>
              {saveStripe.error && (
                <div className="flex items-start gap-2 text-sm text-jarvis-red">
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{(saveStripe.error as Error).message}</span>
                </div>
              )}
              <p className="text-xs text-jarvis-muted">
                Skip this if you don't use Stripe yet. You can connect later.
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-jarvis-border/50">
            <button
              onClick={back}
              disabled={stepIdx === 0 || isSaving}
              className="text-sm px-4 py-2 text-jarvis-muted hover:text-jarvis-text disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            >
              <ArrowLeft size={14} /> Back
            </button>

            <div className="flex items-center gap-2">
              {!step.required && (
                <button
                  onClick={skip}
                  disabled={isSaving}
                  className="text-sm px-4 py-2 text-jarvis-muted hover:text-jarvis-text disabled:opacity-50 transition-colors flex items-center gap-1.5"
                >
                  <SkipForward size={14} /> Skip for now
                </button>
              )}
              <button
                onClick={next}
                disabled={!canProceed || isSaving}
                className={cn(
                  'text-sm px-5 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50',
                  isLast
                    ? 'bg-jarvis-blue hover:bg-jarvis-blue-bright text-black shadow-lg shadow-jarvis-blue/20'
                    : 'bg-jarvis-blue hover:bg-jarvis-blue-bright text-black',
                )}
              >
                {isSaving && <Loader2 size={14} className="animate-spin" />}
                {isLast ? (
                  <>
                    <Rocket size={14} /> Activate {persona.wordmark}
                  </>
                ) : (
                  <>
                    Continue <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
          </div>
        </JarvisCard>

        <p className="text-center text-xs text-jarvis-muted mt-6">
          Your keys are encrypted at rest with a key never exposed to your browser.
        </p>
      </div>
    </div>
  )
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string
  hint?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-jarvis-muted mb-1.5 flex items-center gap-1.5">
        {label}
        {required && <span className="text-jarvis-amber">·</span>}
      </span>
      {children}
      {hint && <p className="text-xs text-jarvis-muted mt-1.5">{hint}</p>}
    </label>
  )
}
